import { useRef, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Controller, useForm } from 'react-hook-form'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import { useQueryState } from 'nuqs'

import { useDialogState } from '@/shared/hooks/useDialogState'
import { useSetToast } from '@/shared/hooks/useSetToast'
import { isErrorWithCode } from '@/shared/utils/queryUtils'
import { useMe } from '@/shared/hooks/useMe'
import { useLocalization } from '@/shared/hooks/useLocalization'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'
import Select from '@/shared/v2/shared/components/Select'
import Banner from '@/shared/v2/shared/components/Banner'
import {
    DrawerBody,
    DrawerContainer,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerRoot,
    DrawerTrigger
} from '@/shared/v2/shared/components/Drawer/Drawer'
import { useWorkflowConfigs } from '../hooks/useImports'
import { useImportUpload } from '../hooks/useImportUpload'
import { WorkflowConfig, WorkflowType, getWorkflowLabel } from '../models/importModels'
import ImportMetaFields from './ImportMetaFields'
import ImportStatusBadge from './ImportStatusBadge'
import UploadProgressSteps from './UploadProgressSteps'
import WorkflowDetails from './WorkflowDetails'

export type FormValues = {
    workflowConfigId: WorkflowType | null
    files: File[] | undefined
    assetFiles: File[] | undefined
}

const NewImportDrawer = () => {
    const t = useTranslations('imports')
    const toasterTranslations = useTranslations('toasters')
    const setToast = useSetToast()
    const { companyId } = useMe()
    const workflowConfigsQuery = useWorkflowConfigs({ companyId })

    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
        defaultValues: { workflowConfigId: null, files: undefined, assetFiles: undefined }
    })

    const [, setImportJobId] = useQueryState('importJobId')

    const { isOpen, onOpenChange } = useDialogState({
        onClose: () => {
            reset()
            if (!isUploading) resetUpload()
        }
    })

    const {
        upload,
        step,
        error,
        reset: resetUpload,
        isUploading
    } = useImportUpload({
        companyId,
        onSuccess: (jobId) => {
            onOpenChange(false)
            setToast({
                intent: 'success',
                title: t('newImport.toast.completed'),
                action: {
                    label: t('newImport.toast.view'),
                    onClick: () => setImportJobId(jobId)
                }
            })
            resetUpload()
        },
        onError: (error) => {
            const errorCode = isErrorWithCode(error) ? `errors.${error.code}` : undefined
            const description = errorCode && t.has(errorCode) ? t(errorCode) : error.message

            setToast({
                title: toasterTranslations('generic.negative.title'),
                description,
                intent: 'error'
            })
        }
    })

    const uploadStartedAt = useRef<string>('')

    useEffect(() => {
        if (!isUploading) return
        const handler = (e: BeforeUnloadEvent) => e.preventDefault()
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [isUploading])

    const onSubmit = async (values: FormValues) => {
        if (!values.workflowConfigId || !values.files?.[0]) return
        uploadStartedAt.current = new Date().toISOString()
        await upload(values.workflowConfigId, values.files[0], values.assetFiles)
    }

    const enabledConfigs = (workflowConfigsQuery.data?.body || []).filter((wc) => wc.enabled)
    const workflowOptions = enabledConfigs.map((wc) => ({
        value: wc.id,
        label: getWorkflowLabel(t, wc.workflowType)
    }))

    const watchedFiles = watch('files')
    const watchedWorkflow = watch('workflowConfigId')
    const isFormValid = !!watchedWorkflow && !!watchedFiles?.[0]
    const showProgress = step !== 'idle' && step !== 'error'

    const selectedConfig: WorkflowConfig | undefined = enabledConfigs.find((wc) => wc.id === watchedWorkflow)
    const { localizeDate } = useLocalization()

    const workflowLabel = selectedConfig ? getWorkflowLabel(t, selectedConfig.workflowType) : ''

    return (
        <DrawerRoot open={isOpen} onOpenChange={onOpenChange}>
            <DrawerTrigger asChild>
                <PrivilegesButton
                    data-testid="__new-import-trigger__"
                    className="whitespace-nowrap font-sans text-[14px]"
                >
                    {t('newImport.title')}
                </PrivilegesButton>
            </DrawerTrigger>

            <DrawerContent data-testid="__new-import-drawer__">
                <DrawerContainer as="form" onSubmit={handleSubmit(onSubmit)}>
                    <DrawerHeader>
                        {showProgress && watchedFiles?.[0]?.name ? watchedFiles[0].name : t('newImport.title')}
                    </DrawerHeader>

                    <DrawerBody>
                        {showProgress ? (
                            <div className="grid grid-cols-1 gap-6">
                                <ImportMetaFields
                                    status={<ImportStatusBadge status="processing" />}
                                    workflowLabel={workflowLabel}
                                    importDate={
                                        uploadStartedAt.current ? (localizeDate(uploadStartedAt.current) ?? '-') : '-'
                                    }
                                />
                                <div className="rounded-lg border border-borderPrimary p-4">
                                    <UploadProgressSteps step={step} />
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6">
                                {error && (
                                    <Banner
                                        intent="negative"
                                        icon={AlertTriangle}
                                        title={t('newImport.error.headerValidation')}
                                        description={
                                            <details className="group">
                                                <summary className="cursor-pointer text-sm text-textSecondary list-none flex items-center gap-1">
                                                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                                                    {t('newImport.error.showDetails')}
                                                </summary>
                                                <p className="mt-2 text-sm text-textSecondary wrap-break-word">
                                                    {isErrorWithCode(error) && t.has(`errors.${error.code}`)
                                                        ? t(`errors.${error.code}`)
                                                        : error.message}
                                                </p>
                                            </details>
                                        }
                                        dataTestId="__new-import-error__"
                                    />
                                )}

                                <Controller
                                    name="workflowConfigId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            data-testid="__new-import-workflow-select__"
                                            label={t('newImport.workflow.label')}
                                            placeholder={t('newImport.workflow.placeholder')}
                                            options={workflowOptions}
                                            value={workflowOptions.find((o) => o.value === value) || null}
                                            onChange={(option) => onChange(option?.value || null)}
                                            isLoading={workflowConfigsQuery.isLoading}
                                        />
                                    )}
                                />

                                {selectedConfig && (
                                    <WorkflowDetails selectedConfig={selectedConfig} control={control} />
                                )}
                            </div>
                        )}
                    </DrawerBody>

                    {selectedConfig && !showProgress && (
                        <DrawerFooter>
                            <LoadingButton
                                data-testid="__new-import-submit__"
                                type="submit"
                                isLoading={isUploading}
                                disabled={!isFormValid || isUploading}
                                className="w-full"
                            >
                                {t('newImport.actions.submit')}
                            </LoadingButton>
                        </DrawerFooter>
                    )}
                </DrawerContainer>
            </DrawerContent>
        </DrawerRoot>
    )
}

export default NewImportDrawer
